/****
 * Main dashboard component responsible for rendering the user's
 * personalized workspace, available widgets, role-based access,
 * mobile experience, and dashboard-level interactions.
 *
 * Handles feature-based widget visibility, responsive layouts,
 * impersonation banners, user access validation, and dashboard
 * navigation while displaying content based on the user's
 * permissions and assigned features.
 */
import { useMsal } from "@azure/msal-react";
import { ChevronRight } from "@untitledui/icons";
import React, { Suspense, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/hooks/useAuth";
import { useDeviceType } from "@/hooks/useDeviceType";
import DashboardSkeleton from "@/shared/components/DashboardSkeleton";
import WidgetLayout from "@/shared/components/WidgetLayout";
import { widgetComponentMap } from "@/types/index";

import { selectFeatures, selectSelectedRole, selectAccessibilityOpen, selectFluxxUserId, resetRoleState } from "../store/userSlice";
import FeedbackButton from "@/features/Feedback/Components/FeedbackButton";
import { selectIsImpersonating } from "@/store/adminSlice";
import ImpersonationBanner from "@/features/Navbar/components/ImpersonationBanner";
import { setAccount, setStatus } from "@/store/authSlice";
import { useAppDispatch } from "@/store/hooks";

const Dashboard: React.FC = () => {
  const { account } = useAuth();
  const { accounts } = useMsal();
  const { isMobile, isTablet, isDesktop } = useDeviceType(); //  added isMobile
  const { t } = useTranslation();
   const dispatch = useAppDispatch();

  useEffect(() => {
    document.title = "Daraja Platform";
  }, []);

  const displayName =
    accounts[0]?.name || account?.displayName || t("components.navBar.header.guest");
  const userUpn = account?.username || "";

  const dashboardFeatures = useSelector(selectFeatures);
  const accessibilityOpen = useSelector(selectAccessibilityOpen);
  const navigate = useNavigate();
  const fluxxUserId = useSelector(selectFluxxUserId);
  const hasFluxxUserId = !!fluxxUserId;
  const isImpersonating = useSelector(
  selectIsImpersonating
);



  /**
   * Access map
   */
  // Creates a quick lookup map to determine feature access permissions
  const featureAccessMap = useMemo(() => {
    const map: Record<string, boolean> = {};

    dashboardFeatures.forEach((feature: { feature_code: string; access: boolean | number }) => {
      map[feature.feature_code] = feature.access === true || feature.access === 1;
    });

    return map;
  }, [dashboardFeatures]);
  // Opens the access request portal when a user needs additional permissions
  const handleRequestAccess = () => {
  window.open(
    "https://mastercardfdn.service-now.com/pulse",
    "_blank",
    "noopener,noreferrer"
  );
};

const { instance } = useMsal();
// Clears user session data and redirects to the login page
const handleSignOut = async () => {
  dispatch(resetRoleState());
  dispatch(setStatus("signedOut"));
  dispatch(setAccount(null));

  await instance.clearCache();

  navigate("/login", { replace: true });
};
// Checks whether the user has access to at least one dashboard feature
const hasRoles = dashboardFeatures?.some(
  (feature) => featureAccessMap[feature.feature_code]
);

  /**
   *  MOBILE LIST (Quick Actions)
   */
  // Returns widgets configured to be visible on mobile devices
const getMobileWidgets = () => {
  return dashboardFeatures.filter(
    (feature) =>
      feature.access &&
      feature.mobile_visible
  );
};

  /**
   *  RENDER MOBILE VIEW
   */
  // Renders the simplified mobile dashboard experience as quick action cards
  const renderMobileView = () => {
    const mobileWidgets = getMobileWidgets();

    return (
      <div className="px-4 py-4">
        <h2 className="text-lg font-semibold mb-4">{t("components.dashboard.QuickActions")}</h2>

        <div className="space-y-4">
         {mobileWidgets
  .filter((feature) => feature.route_path)
  .map((feature) => (
    <div
      key={feature.feature_code}
      onClick={() => navigate(`/app${feature.route_path}`)}
      className="flex justify-between items-center p-4 rounded-xl border bg-white shadow-sm cursor-pointer"
    >
      <span className="font-medium">
        {t(feature.title_key)}
      </span>

      <ChevronRight className="w-5 h-5 text-gray-500" />
    </div>
))}
        </div>
      </div>
    );
  };

  /**
   *  DESKTOP WIDGET RENDER (UNCHANGED)
   */
  // Renders dashboard widgets based on user permissions and device type
  const renderWidgets = () =>
    dashboardFeatures.map((feature) => {
      // Skip widgets that the user does not have permission to access
      if (!featureAccessMap[feature.feature_code]) return null;
      // Hide widgets that are not configured to appear on tablet devices
    if (
  isTablet &&
  feature.tablet_visible === false
) {
  return null;
}

    // Resolve the widget component dynamically using the configured component name
    const Component =
  widgetComponentMap[feature.widget_component];

if (!Component) return null;

      return (
        <WidgetLayout
   layout={isDesktop ? feature.layout : "3"}
>
          <div id={feature.feature_code}>
            <Component userUpn={userUpn} />
          </div>
        </WidgetLayout>
      );
    });

  /**
   *  FINAL RETURN
   */
  return (
    <main className="bg-[var(--bg-main)] text-[var(--text-color)]">
      {/*  COMMON HEADER (DESKTOP + MOBILE with responsive sizing) */}
      <div className="px-4 pt-4 pb-2 sm:px-6 sm:pt-1">
       {/* Display impersonation banner when the user is acting on behalf of another user*/} 
  {isImpersonating ? (
    <ImpersonationBanner />
  ) : (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-4 py-4 sm:px-6 sm:py-4">
      <h2 className="text-xl font-bold sm:text-lg sm:font-semibold">
        {t("components.dashboard.welcomeMessage")}{" "}
        {displayName}!
      </h2>
    </div>
  )}
  {/* Show a warning when the user does not have a valid Fluxx account mapping */}
  {!hasFluxxUserId && (
    <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-4 shadow-sm">
      <p className="text-sm text-red-700">
        {t("components.dashboard.noFluxxIdMessage")}{" "}
        <a
          href="https://mastercardfdn.service-now.com/pulse"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-red-600 hover:text-blue-700 underline"
        >
        {t("components.dashboard.hyperLinklabel")}
        </a>
      </p>
    </div>
  )}
    {!hasRoles && (
  <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
    <div className="w-full max-w-lg rounded-xl bg-white p-8 shadow-2xl">
      <h2 className="mb-4 text-2xl font-semibold text-gray-900">
        {t("components.dashboard.noAccessTitle")}
      </h2>

      <p className="mb-6 text-sm text-gray-600">
        {t("components.dashboard.noAccessMessage")}
      </p>

      <div className="flex justify-end gap-3">
        <button
          onClick={handleSignOut}
          className="rounded-lg border border-gray-300 px-5 py-2 font-medium text-gray-700 hover:bg-gray-50"
        >
          {t("components.dashboard.noAccessSignOutButton")}
        </button>

        <button
          onClick={handleRequestAccess}
          className="rounded-lg bg-[var(--color-brand)] px-5 py-2 font-medium text-white hover:opacity-90"
        >
          {t("components.dashboard.noAccessRequestButton")}
        </button>
      </div>
    </div>
  </div>
)}
</div>


      {/*  CONTENT SWITCH */}
      <section className="dashboard-grid px-4 py-4">
        {/* Render a mobile-friendly dashboard or the standard widget grid based on device type */}
        {isMobile ? (
          renderMobileView()
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Suspense fallback={<DashboardSkeleton />}>{renderWidgets()}</Suspense>
          </div>
        )}
      </section>

      {/*  FOOTER (HIDDEN ON MOBILE) */}
      {/* <div className="hidden sm:block w-full bg-neutral-100 px-8 py-4 text-sm text-neutral-700">
        <span className="font-semibold">{t("components.footer.links.accessibility")}:</span>
        <span>
          {" "}
          {t("components.footer.message")}{" "}
          <a
            href="https://mastercardfdn.org/en/contact/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--color-brand)] hover:underline"
          >
            https://mastercardfdn.org/en/contact/
          </a>
        </span>
      </div> */}
      {featureAccessMap["dashboard.feedback"] && !accessibilityOpen && <FeedbackButton />}
    </main>
  );
};

export default Dashboard;
